import React, { useEffect, useState, useMemo, useCallback } from "react";
import api from "../services/api";
import { Link, useNavigate } from "react-router-dom";
import "./WorkTimes.css";
import "./OrganizationTree.css";

/** Python weekday に合わせた月曜始まり（0=月 … 6=日） */
const DAY_NAMES_JAPANESE = ["月", "火", "水", "木", "金", "土", "日"];

function toISODateLocal(y, m0, day) {
  const m = String(m0 + 1).padStart(2, "0");
  const d = String(day).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * API の勤怠行を、start～end の全日（土日・未打刻も含む）に広げる（合計労働時間画面・個別印刷）。
 */
function expandRecordsToCalendarDays(records, startStr, endStr) {
  const map = new Map();
  for (const r of records || []) {
    const key = (r.date || "").slice(0, 10);
    if (key) map.set(key, r);
  }
  const [ys, ms, ds] = startStr.split("-").map((x) => parseInt(x, 10));
  const [ye, me, de] = endStr.split("-").map((x) => parseInt(x, 10));
  const out = [];
  const cur = new Date(ys, ms - 1, ds, 12, 0, 0);
  const end = new Date(ye, me - 1, de, 12, 0, 0);
  for (; cur <= end; cur.setDate(cur.getDate() + 1)) {
    const iso = toISODateLocal(cur.getFullYear(), cur.getMonth(), cur.getDate());
    const existing = map.get(iso);
    if (existing) {
      out.push({ ...existing, _calendarFill: false });
      continue;
    }
    const jsDay = cur.getDay();
    const monday0 = (jsDay + 6) % 7;
    const isWeekend = jsDay === 0 || jsDay === 6;
    out.push({
      _calendarFill: true,
      id: null,
      date: iso,
      day_of_week: DAY_NAMES_JAPANESE[monday0],
      start_time: null,
      end_time: null,
      total_hours: 0,
      is_holiday: isWeekend,
      is_cross_day: false,
      adminNote: null,
    });
  }
  return out;
}

// ヒートマップは各セルを「出勤(左) / 退勤(右)」の2分割で色分けする
const HEATMAP_IN_COLORS = { off: "#f3f4f6", missing: "#fecaca", late: "#fef08a", ok: "#bbf7d0" };
const HEATMAP_OUT_COLORS = { off: "#f3f4f6", missing: "#fed7aa", ok: "#bbf7d0" };
const HEATMAP_IN_LABELS = { off: "予定なし", missing: "出勤打刻なし", late: "遅刻", ok: "出勤OK" };
const HEATMAP_OUT_LABELS = { off: "予定なし", missing: "退勤打刻なし", ok: "退勤OK" };

// セル(オブジェクト)から出勤側・退勤側の状態キーを求める（旧データの文字列にも一応対応）
const heatmapInState = (c) =>
  !c || typeof c !== "object" || c.off ? "off" : !c.in ? "missing" : c.late ? "late" : "ok";
const heatmapOutState = (c) =>
  !c || typeof c !== "object" || c.off ? "off" : !c.out ? "missing" : "ok";

// 凡例（各セル左＝出勤 / 右＝退勤）
const HEATMAP_LEGEND = [
  { color: "#bbf7d0", label: "出勤・退勤OK" },
  { color: "#fef08a", label: "遅刻（出勤）" },
  { color: "#fecaca", label: "出勤打刻なし" },
  { color: "#fed7aa", label: "退勤打刻なし" },
  { color: "#f3f4f6", label: "予定なし" },
];

/**
 * スケジュールが休日／有給のとき、出社・退社欄に出す表示（API の scheduleDayKind 等）。
 * 戻り値: { text, bothColumns } または null（通常の打刻表示）
 */
function scheduleRestDisplay(row) {
  if (!row || typeof row !== "object") return null;
  const k = row.scheduleDayKind ?? row.schedule_day_kind;
  const label = String(
    row.scheduleLabel ?? row.schedule_label ?? row.shiftName ?? row.shift_name ?? row.leaveType ?? row.leave_type ?? ""
  );
  const paid =
    row.isPaidLeave === true ||
    row.paidLeave === true ||
    row.is_paid_leave === true ||
    /有給|休暇|paid|annual|vacation/i.test(label);
  if (k === "paid" || k === "leave" || k === "paid_leave" || k === "paidLeave") {
    return { text: "有給", bothColumns: true };
  }
  if (k === "off" || k === "holiday" || k === "day_off" || k === "dayoff") {
    return { text: paid ? "有給" : "休み", bothColumns: true };
  }
  if (paid && (k == null || k === "")) {
    return { text: "有給", bothColumns: true };
  }
  return null;
}

/** 勤務表「合計」列の値 ＝ 労働時間 ＋ 残業 − 遅刻 − 早退。
 *
 *  月の合計もこの関数で積み上げること。以前は月合計だけ total_hours（＝労働時間のみ）
 *  を単純加算していたため、表の「合計」列を縦に足した値と月合計が一致しなかった。
 *  残業・遅刻・早退は、管理者が申請を承認して時間を確定した場合（adminNote）は
 *  そちらを優先する。列の表示と同じ優先順位。 */
function calcRowTotalHours(r) {
  const note = r?.adminNote;
  const laborHours = Number(r?.total_hours ?? 0);
  const approvedOvertimeMins   = note?.overtimeMinutes   ?? null;
  const approvedEarlyLeaveMins = note?.earlyLeaveMinutes ?? null;
  const approvedLateMins       = note?.lateMinutes       ?? null;
  const overtimeHours = approvedOvertimeMins !== null
    ? approvedOvertimeMins / 60
    : Number(r?.overtime_hours ?? r?.overtimeHours ?? 0);
  const earlyLeaveHours = approvedEarlyLeaveMins !== null
    ? approvedEarlyLeaveMins / 60
    : Number(r?.early_leave_hours ?? r?.earlyLeaveHours ?? 0);
  const lateHours = approvedLateMins !== null
    ? approvedLateMins / 60
    : Number(r?.late_hours ?? r?.lateHours ?? 0);
  return laborHours + overtimeHours - lateHours - earlyLeaveHours;
}

const WorkTimes = () => {
  const [viewMode, setViewMode] = useState("department_selection");
  const [loading, setLoading] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [displayDate, setDisplayDate] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const d = params.get("date");
      if (d && /^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
    } catch (_) {
      /* ignore */
    }
    return new Date().toISOString().split("T")[0];
  });
  const [showDeptList, setShowDeptList] = useState(false);
  const [selectedDepartmentName, setSelectedDepartmentName] = useState(null);
  const [employeeRecords, setEmployeeRecords] = useState([]);
  const [employeeDetail, setEmployeeDetail] = useState(null);
  const [detailDateRange, setDetailDateRange] = useState({ start: "", end: "" });
  const navigate = useNavigate();
  // States for Checkbox and In
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [printData, setPrintData] = useState([]);
  const [isPrinting, setIsPrinting] = useState(false);
  /** 印刷期間の終了日（デフォルトは今日） */
  const [printEndDate, setPrintEndDate] = useState(() => new Date().toISOString().split("T")[0]);
  /** 印刷期間の開始日（空の場合は終了月の1日を自動計算） */
  const [printStartDate, setPrintStartDate] = useState("");
  const ROWS_PER_PRINT_PAGE = 23;

  // ── 共通ヘルパー（renderEmployeeDetail / renderPrintView 両方で使用）──

  /** 値が0の場合は空文字を返す（「—」は表示しない） */
  const fmtHrs = (h) => (h > 0 ? h.toFixed(2) : "");

  /** 日跨ぎの時刻に付ける接頭辞。33:30 のような24時間超え表記ではなく
   *  「翌9:30」と実際の時計の時刻で見せる（同じ日の朝と区別が付くようにする）。 */
  const NEXT_DAY_PREFIX = "翌";

  /** 予定終了時刻：夜勤（終了 ≦ 開始）は翌日なので「翌」を付ける */
  const formatSchedEndTime = (startStr, endStr) => {
    if (!endStr) return "";
    if (startStr && /^\d{2}:\d{2}/.test(startStr) && /^\d{2}:\d{2}/.test(endStr)) {
      const [sh, sm] = startStr.split(":").map(Number);
      const [eh, em] = endStr.split(":").map(Number);
      const endMins = eh * 60 + em;
      const hhmm = `${eh}:${String(em).padStart(2, "0")}`;
      return endMins <= sh * 60 + sm ? `${NEXT_DAY_PREFIX}${hhmm}` : hhmm;
    }
    return endStr.slice(0, 5);
  };

  /** 実際終了時刻：翌日打刻は「翌」を付けて実際の時刻で表示する。
   *  日付が2日以上ずれている場合は「翌」では正しくないため null を返し、
   *  呼び出し側の通常表示にフォールバックさせる。 */
  const formatActualEndTimeCrossDay = (startIso, endIso) => {
    if (!startIso || !endIso) return null;
    try {
      const s = new Date(startIso);
      const e = new Date(endIso);
      const startDay = new Date(s.getFullYear(), s.getMonth(), s.getDate());
      const endDay = new Date(e.getFullYear(), e.getMonth(), e.getDate());
      const dayDiff = Math.round((endDay - startDay) / 86400000);
      if (dayDiff !== 1) return null;
      return `${NEXT_DAY_PREFIX}${e.getHours()}:${String(e.getMinutes()).padStart(2, "0")}`;
    } catch (_) { return null; }
  };

  /** 18列テーブルの共通ヘッダー */
  const renderDetailTableHeader = () => (
    <thead>
      <tr className="detail-table-header">
        <th>日付</th>
        <th>曜日</th>
        <th>勤務</th>
        <th>開始<br/>時刻</th>
        <th>終了<br/>時刻</th>
        <th>出社<br/>時刻</th>
        <th>退社<br/>時刻</th>
        <th>休憩<br/>時間</th>
        <th>労働<br/>時間</th>
        {/* 並び順は下の renderDetailTableRow のセル順と必ず一致させること。
            残業時間 → 残休時間（＝残業休憩）の順。 */}
        <th>残業<br/>時間</th>
        <th>残休<br/>時間</th>
        <th>深夜<br/>残業</th>
        <th>早出<br/>時間</th>
        <th>休日<br/>出勤</th>
        <th>早退<br/>時間</th>
        {/*<th>その<br/>他時<br/>間</th>*/}
        <th>遅刻<br/>時間</th>
        <th>合計</th>
        <th>備考</th>
      </tr>
    </thead>
  );

  /** 18列テーブルの共通行レンダリング */
  const renderDetailTableRow = (r, index) => {
    const note = r.adminNote;
    const fillOnly = r._calendarFill === true;
    const restDisp = scheduleRestDisplay(r);

    const schedStart = r.scheduleStart ?? r.schedule_start ?? "";
    const schedEnd   = r.scheduleEnd   ?? r.schedule_end   ?? "";
    const shiftName  = r.scheduleLabel ?? r.schedule_label ?? r.shiftName ?? r.shift_name ?? "";
    const scheduleKind = r.scheduleDayKind ?? r.schedule_day_kind;

    const schedStartDisplay = schedStart ? schedStart.slice(0, 5) : "";
    const schedEndDisplay   = schedStart && schedEnd ? formatSchedEndTime(schedStart, schedEnd) : (schedEnd ? schedEnd.slice(0, 5) : "");

    const kinmuDisplay = restDisp
      ? restDisp.text
      : shiftName || (scheduleKind && scheduleKind !== "work" ? scheduleKind : "");

    const clockIn = restDisp && restDisp.bothColumns
      ? restDisp.text
      : r.start_time
        ? formatTimeHhMm(r.start_time)
        : note?.forgotInTime ?? (fillOnly ? "" : "休");

    const clockOut = (() => {
      if (restDisp && restDisp.bothColumns) return restDisp.text;
      if (!r.end_time) {
        if (note?.forgotOutTime != null) return note.forgotOutTime;
        if (fillOnly) return "";
        // 出社打刻がある＝勤務中（退勤忘れ含む）。出社も無ければ「休」（出社列と揃える）。
        return r.start_time ? "勤務中" : "休";
      }
      if (r.is_cross_day && r.start_time) {
        return formatActualEndTimeCrossDay(r.start_time, r.end_time) ?? formatTimeHhMm(r.end_time);
      }
      return formatTimeHhMm(r.end_time);
    })();

    const laborHours       = Number(r.total_hours ?? 0);
    const approvedOvertimeMins   = note?.overtimeMinutes   ?? null;
    const approvedEarlyLeaveMins = note?.earlyLeaveMinutes ?? null;
    const approvedLateMins       = note?.lateMinutes       ?? null;
    const overtimeHours    = approvedOvertimeMins !== null
      ? approvedOvertimeMins / 60
      : Number(r.overtime_hours    ?? r.overtimeHours    ?? 0);
    const nightOtHours     = Number(r.night_overtime_hours ?? r.nightOvertimeHours ?? 0);
    // 残業時間の列は「合計残業時間」(overtimeHours) を表示し、深夜残業はそのうちの内数として
    // 別列に「うち深夜残業」で出す。例: 残業5h のうち 深夜1h → 残業時間=5.0 / うち深夜=1.0。
    // （給与CSVでは割増率が違うため 普通=総残業−深夜 に分けて出力する。そちらは別ロジック。）
    const earlyHours       = Number(r.early_hours       ?? r.earlyHours       ?? 0);
    const holidayHours     = Number(r.holiday_hours     ?? r.holidayHours     ?? 0);
    const earlyLeaveHours  = approvedEarlyLeaveMins !== null
      ? approvedEarlyLeaveMins / 60
      : Number(r.early_leave_hours ?? r.earlyLeaveHours  ?? 0);
    const otherHours       = Number(r.other_hours       ?? r.otherHours       ?? 0);
    const lateHours        = approvedLateMins !== null
      ? approvedLateMins / 60
      : Number(r.late_hours        ?? r.lateHours        ?? 0);
    const punchBreakMins   = Number(r.breakMinutes ?? 0);
    const shiftBreakMins   = Number(r.scheduledBreakMinutes ?? 0);
    const breakHours = punchBreakMins > 0 ? punchBreakMins / 60
                     : shiftBreakMins > 0 ? shiftBreakMins / 60 : 0;

    const overtimeBreakRaw = r.overtimeBreakHours ?? r.overtime_break_hours ?? note?.overtimeBreakHours ?? note?.overtimeBreak ?? 0;
    const overtimeBreakHours = (() => {
      if (typeof overtimeBreakRaw === 'string' && overtimeBreakRaw.includes(':')) {
        const [h, m] = overtimeBreakRaw.split(':').map(Number);
        return (h || 0) + (m || 0) / 60;
      }
      return Number(overtimeBreakRaw || 0);
    })();

    const hoursDisplay = fillOnly && laborHours === 0 ? "" : laborHours.toFixed(2);
    const totalCalc    = calcRowTotalHours(r);
    const totalDisplay = fillOnly && laborHours === 0 ? "" : totalCalc.toFixed(2);

    const adminIn    = Boolean(note?.startModifiedByAdmin && r.start_time);
    const adminOut   = Boolean(note?.endModifiedByAdmin   && r.end_time);
    const adminHours = adminIn || adminOut;

    const bikouParts = [
      r.businessTrip ? "出張" : "",
      note?.forgotInTime  && !r.start_time ? `出勤打刻忘れ(${note.forgotInTime})`  : "",
      note?.forgotOutTime && !r.end_time   ? `退勤打刻忘れ(${note.forgotOutTime})` : "",
      adminIn  ? "出勤修正※" : "",
      adminOut ? "退勤修正※" : "",
      approvedOvertimeMins   !== null ? `残業申請承認済(${Math.floor(approvedOvertimeMins/60)}h${approvedOvertimeMins%60>0?`${approvedOvertimeMins%60}m`:""})` : "",
      approvedLateMins       !== null ? `遅刻申請承認済(${approvedLateMins}分)` : "",
      approvedEarlyLeaveMins !== null ? `早退申請承認済(${approvedEarlyLeaveMins}分)` : "",
    ].filter(Boolean);
    const bikou = bikouParts.join(" / ");

    return (
      <tr
        key={r.date ? `${r.date}-${index}` : index}
        className={r.is_holiday ? "holiday-row" : ""}
      >
        <td>{formatMonthDayShort(r.date)}</td>
        <td>{r.day_of_week}</td>
        <td>{kinmuDisplay}</td>
        <td>{fillOnly ? "" : schedStartDisplay}</td>
        <td>{fillOnly ? "" : schedEndDisplay}</td>
        <td style={adminIn ? { color: "#c62828", fontWeight: "bold" } : note?.forgotInTime && !r.start_time ? { color: "#e65100", fontStyle: "italic" } : {}}>
          {clockIn}{adminIn ? "※" : ""}
        </td>
        <td style={adminOut ? { color: "#c62828", fontWeight: "bold" } : note?.forgotOutTime && !r.end_time ? { color: "#e65100", fontStyle: "italic" } : {}}>
          {clockOut}{adminOut ? "※" : ""}
        </td>
        <td>{fmtHrs(breakHours)}</td>
        <td style={adminHours ? { color: "#c62828", fontWeight: "bold" } : {}}>{hoursDisplay}</td>
        <td>{fmtHrs(overtimeHours)}</td>
        <td>{fmtHrs(overtimeBreakHours)}</td>
        <td>{fmtHrs(nightOtHours)}</td>
        <td>{fmtHrs(earlyHours)}</td>
        <td>{fmtHrs(holidayHours)}</td>
        <td>{fmtHrs(earlyLeaveHours)}</td>
        {/*<td>{fmtHrs(otherHours)}</td>*/}
        <td>{fmtHrs(lateHours)}</td>
        <td>{totalDisplay}</td>
        <td>{bikou}</td>
      </tr>
    );
  };

  /** 打刻忘れ（シフトありで出勤/退勤打刻なし）。管理者が日別勤怠・メモで修正可能 */
  const [forgotPunchRows, setForgotPunchRows] = useState([]);
  /** 当日スケジュール未登録（未設定）の社員（一覧の件数バッジ・スケジュール管理リンク用） */
  const [unregisteredScheduleRows, setUnregisteredScheduleRows] = useState([]);
  const [searchTerm , setSearchTerm] = useState("");
  const [filterDepartment, setFilterDepartment] = useState("");
  /** 部署選択: departmentId (xx00)。課選択: 課の departmentName */
  const [selectedDeptId, setSelectedDeptId] = useState("");
  const [selectedKaName, setSelectedKaName] = useState("");
  const selectedDept = useMemo(
    () => (Array.isArray(departments) ? departments.find((d) => d.departmentId === selectedDeptId) : null),
    [departments, selectedDeptId]
  );
  /** APIに渡す部署名（課選択時は課名、それ以外は部署名） */
  const effectiveDepartmentName = selectedKaName || (selectedDept?.departmentName ?? "") || "";
  const departmentList = useMemo(() => {
      if (employeeRecords.length === 0) return [];
        const departments = [...new Set(employeeRecords.map(e => e.departmentName).filter(name => name))];
        return departments.sort(); // 名前順にソート
      }, [employeeRecords]);

  const scheduleUnregisteredHref = useMemo(() => {
    const q = new URLSearchParams();
    q.set("date", displayDate);
    if (effectiveDepartmentName) q.set("departmentName", effectiveDepartmentName);
    return `/admin/workstime/schedule?${q.toString()}`;
  }, [displayDate, effectiveDepartmentName]);

  const [mainTab, setMainTab] = useState("records");

  const nowForAnalytics = new Date();
  const defaultMonthForAnalytics = `${nowForAnalytics.getFullYear()}-${String(nowForAnalytics.getMonth() + 1).padStart(2, "0")}`;
  const [analyticsMonth, setAnalyticsMonth] = useState(defaultMonthForAnalytics);
  const [analyticsDept, setAnalyticsDept] = useState("__ALL__");
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState("");
  const [analyticsData, setAnalyticsData] = useState(null);

  const analyticsDepartmentOptions = useMemo(() => {
    const names = new Set();
    for (const d of departments || []) {
      if (d.departmentName) names.add(d.departmentName);
      for (const c of d.children || []) {
        if (c.departmentName) names.add(c.departmentName);
      }
    }
    return Array.from(names).sort((a, b) => a.localeCompare(b, "ja"));
  }, [departments]);

  const fetchAttendanceAnalytics = useCallback(async () => {
    setAnalyticsLoading(true);
    setAnalyticsError("");
    try {
      const params = { month: analyticsMonth };
      if (analyticsDept && analyticsDept !== "__ALL__") {
        params.departmentName = analyticsDept;
      }
      const res = await api.get("/api/worktime/admin/attendance-analytics/", { params });
      setAnalyticsData(res.data);
    } catch (err) {
      console.error(err);
      setAnalyticsError(
        err.response?.data?.message || "勤怠分析データの取得に失敗しました。"
      );
      setAnalyticsData(null);
    } finally {
      setAnalyticsLoading(false);
    }
  }, [analyticsMonth, analyticsDept]);

  useEffect(() => {
    if (mainTab !== "analytics") return;
    fetchAttendanceAnalytics();
  }, [mainTab, fetchAttendanceAnalytics]);

  useEffect(() => {
    const fetchDepartments = async () => {
      setLoading(true);
      try {
        const res = await api.get("/api/departments/");
        setDepartments(res.data);
      } catch (err) {
        console.error("部署名の取得エラー:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchDepartments();
  }, []);

  
  useEffect(() => {
    if (employeeRecords.length === 0) {
      setSelectAll(false);
      return;
    }
    const allSelected = employeeRecords.every(r => selectedEmployeeIds.includes(r.memberId));
    setSelectAll(allSelected);
  }, [selectedEmployeeIds, employeeRecords]);

  const fetchEmployeeList = async (departmentNameOrEmpty, date) => {
    const departmentName = departmentNameOrEmpty || "";
    setLoading(true);
    setSelectedDepartmentName(departmentName || "");
    setEmployeeRecords([]);
    setSelectedEmployeeIds([]);
    setShowDeptList(false);

    try {
      const departmentParam = departmentName || "ALL";
      const deptParam = departmentParam === "ALL" ? undefined : departmentParam;
      const [listRes, todayRes, dailyRes] = await Promise.all([
        api.get(`/api/admin/employees/by_department_name/${departmentParam}/`, { params: { date } }),
        api.get("/api/admin/worktime/today/", { params: { date, departmentName: deptParam } }),
        api.get("/api/worktime/admin/daily/", { params: { date, departmentName: deptParam } }),
      ]);
      let unregEmployees = [];
      try {
        const unregRes = await api.get("/api/worktime/admin/unregistered-schedule-list/", {
          params: { date, departmentName: deptParam },
        });
        unregEmployees = Array.isArray(unregRes.data?.employees) ? unregRes.data.employees : [];
      } catch (unregErr) {
        if (unregErr.response?.status !== 404) {
          console.error("スケジュール未登録一覧の取得エラー:", unregErr);
        }
      }
      const monthlyByMember = (todayRes.data && todayRes.data.monthly_summary) || {};
      const mapped = listRes.data.map((r) => ({
        memberId: r.memberId,
        name: r.employeeName,
        departmentName: r.departmentName,
        start_time: r.startTime,
        total_hours: r.totalHours,
        /** 当日の実打刻を管理者が修正した場合（打刻忘れ管理メモ） */
        admin_punch_modified: Boolean(r.adminPunchModified),
        /** 当月総合労働時間（昨日まで）。打刻画面ではこの値を表示 */
        monthly_total_to_yesterday: monthlyByMember[r.memberId]?.total_hours ?? null,
        punchTimeDisplay: r.punchTimeDisplay ?? "",
        scheduleDayKind: r.scheduleDayKind ?? r.schedule_day_kind,
        scheduleLabel: r.scheduleLabel ?? r.schedule_label,
        isPaidLeave: r.isPaidLeave ?? r.is_paid_leave,
      }));
      setEmployeeRecords(mapped);
      setIsPrinting(false);

      const dailyRows = Array.isArray(dailyRes.data) ? dailyRes.data : [];
      const forgot = dailyRows.filter(
        (row) => {
          const dk = row.scheduleDayKind ?? row.schedule_day_kind;
          const isWork = !dk || dk === "work";
          return (
            isWork &&
            (row.scheduleStart || row.scheduleEnd) &&
            (!row.startTime || !row.endTime)
          );
        }
      );
      setForgotPunchRows(forgot);
      setUnregisteredScheduleRows(unregEmployees);
    } catch (err) {
      console.error("部署情報が見つかりません:", err);
      setForgotPunchRows([]);
      setUnregisteredScheduleRows([]);
    } finally {
      setLoading(false);
    }
  };

  // 一覧の「日付（当日）」の属する月の1日～その日まで（前月分は出さない）
  const fetchEmployeeDetail = async (memberId, start = null, end = null) => {
    setLoading(true);
    let startDate, endDate;

    if (!start && !end) {
      const anchor = new Date(`${displayDate}T12:00:00`);
      // toISOString は UTC 換算で前日になるため、月初はローカルで YYYY-MM-DD を組み立てる
      startDate = toISODateLocal(anchor.getFullYear(), anchor.getMonth(), 1);
      endDate = displayDate;
    } else {
      startDate = start;
      endDate = end;
    }

    try {
      const params = { start: startDate, end: endDate };
      setDetailDateRange({ start: startDate, end: endDate });
      const res = await api.get(`/api/admin/worktime/${memberId}/`, { params });
      setEmployeeDetail(res.data);
      setViewMode("employee_detail");
    } catch (err) {
      console.error("社員情報を見つかりません:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDepartmentChange = (e) => {
    const deptId = e.target.value;
    setSelectedDeptId(deptId);
    setSelectedKaName("");
    const dept = departments.find((d) => d.departmentId === deptId);
    const name = dept ? dept.departmentName : "";
    setFilterDepartment(name);
    setSearchTerm("");
    if (name) fetchEmployeeList(name, displayDate);
    else fetchEmployeeList("", displayDate); // 全ての部署（ALL）を表示
  };

  const handleKaChange = (e) => {
    const kaName = e.target.value;
    setSelectedKaName(kaName);
    setFilterDepartment(kaName || (selectedDept?.departmentName ?? ""));
    setSearchTerm("");
    const name = kaName || (selectedDept?.departmentName ?? "");
    if (name) fetchEmployeeList(name, displayDate);
    else fetchEmployeeList("", displayDate); // 全ての部署（ALL）を表示
  };

  useEffect(() => {
    if (departments.length > 0 && employeeRecords.length === 0 && !effectiveDepartmentName) {
      fetchEmployeeList("", displayDate);
    }
  }, [departments, displayDate]);

  const formatJapaneseDate = (isoDate) => {
    const d = new Date(isoDate);
    return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`;
  };

  const formatMonthDay = (isoDate) => {
    const d = new Date(isoDate);
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const day = d.getDate().toString().padStart(2, '0');
    return `${month}/${day}`;
  };

  /** 印刷用 M/D（例: 3/1） */
  const formatMonthDayShort = (isoDate) => {
    const d = new Date(`${isoDate.slice(0, 10)}T12:00:00`);
    return `${d.getMonth() + 1}/${d.getDate()}`;
  };

  /** 出社・退社時刻を hh:mm で表示 */
  const formatTimeHhMm = (isoDateTimeOrNull) => {
    if (!isoDateTimeOrNull) return "";
    const d = new Date(isoDateTimeOrNull);
    const h = d.getHours();
    const m = d.getMinutes();
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  };

  /** 印刷用：期間を「○年○月○日 ～ ○年○月○日」で表示（実際の開始日を反映） */
  const formatPrintPeriod = (printRange) => {
    if (!printRange?.end) return '';
    const e = new Date(`${String(printRange.end).slice(0, 10)}T12:00:00`);
    const ey = e.getFullYear(), em = e.getMonth() + 1, ed = e.getDate();
    if (printRange.start) {
      const s = new Date(`${String(printRange.start).slice(0, 10)}T12:00:00`);
      return `${s.getFullYear()}年${s.getMonth() + 1}月${s.getDate()}日 ～ ${ey}年${em}月${ed}日`;
    }
    // 開始日が無い場合のみ、終了月の1日から
    return `${ey}年${em}月1日 ～ ${ey}年${em}月${ed}日`;
  };

  /** 印刷用：M/D～M/D（例: 3/1～3/31） */
  const formatPrintPeriodShort = (printRange) => {
    if (!printRange?.start || !printRange?.end) return "";
    const s = new Date(`${printRange.start.slice(0, 10)}T12:00:00`);
    const e = new Date(`${printRange.end.slice(0, 10)}T12:00:00`);
    return `${s.getMonth() + 1}/${s.getDate()} ～ ${e.getMonth() + 1}/${e.getDate()}`;
  };

  const handleBack = () => {
    navigate(`/dashboard`);
  }

  // Logic Checkbox
  const handleSelectEmployee = (memberId) => {
    setSelectedEmployeeIds(prev => {
      if (prev.includes(memberId)) {
        return prev.filter(id => id !== memberId);
      } else {
        return [...prev, memberId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedEmployeeIds([]);
    } else {
      const allIds = employeeRecords.map(r => r.memberId);
      setSelectedEmployeeIds(allIds);
    }
  };

 
  const handlePrintSelected = async () => {
    if (selectedEmployeeIds.length === 0) {
      alert("最低が一人選択ください .");
      return;
    }

    setLoading(true);
    const printRecords = [];
    const now = new Date();
    const endDate = printEndDate || now.toISOString().split("T")[0];
    const endD = new Date(endDate);
    const startDate = printStartDate || toISODateLocal(endD.getFullYear(), endD.getMonth(), 1);

    try {
    const failedIds = [];
    for (const memberId of selectedEmployeeIds) {
      const employeeInfo = employeeRecords.find(e => e.memberId === memberId);
      if (!employeeInfo) continue;
      try {
        const res = await api.get(`/api/admin/worktime/${memberId}/`, {
          params: { start: startDate, end: endDate },
        });
        const data = res.data || {};
        const expanded = expandRecordsToCalendarDays(data.records ?? [], startDate, endDate);
        printRecords.push({
          memberId: data.memberId ?? memberId,
          name: data.name ?? employeeInfo.name ?? memberId,
          departmentName: data.departmentName ?? employeeInfo.departmentName ?? "",
          records: expanded,
          total_hours: data.total_hours ?? 0,
          summary: data.summary ?? null,
          printRange: { start: startDate, end: endDate },
        });
      } catch (err) {
        console.error("データ取得エラー:", memberId, err);
        failedIds.push(employeeInfo.name || memberId);
        printRecords.push({
          memberId,
          name: employeeInfo.name ?? memberId,
          departmentName: employeeInfo.departmentName ?? "",
          records: [],
          total_hours: 0,
          printRange: { start: startDate, end: endDate },
        });
      }
    }
    if (failedIds.length > 0) {
      alert(`以下の社員は勤怠データを取得できませんでした（空で印刷します）:\n${failedIds.join("\n")}`);
    }
    setPrintData(printRecords);
    setIsPrinting(true);
    } catch (err) {
      console.error("データ取得エラー:", err);
      alert("データ取得エラー: " + (err?.response?.data?.detail || err?.message || ""));
    } finally {
      setLoading(false);
    }
  };

  /*-- csvファイル出力 --*/
  /** CSVの1項目。カンマ・引用符・改行を含むときだけ引用符で囲む。
   *  弥生のエクスポート（docs/yayoikyuyo.csv）も、社員コードは素のまま・
   *  金額 "9,120" のようにカンマを含むものだけ囲む形式のためそれに合わせる。 */
  const csvQuote = (v) => {
    const s = String(v ?? "");
    return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };

  /** 数値の表記。弥生のエクスポート（docs/yayoikyuyo.csv）に合わせ、
   *  末尾の 0 を落とす（156.00 → 156、173.50 → 173.5）。 */
  const csvNum = (n) => {
    const v = Number(n) || 0;
    return String(Math.round(v * 100) / 100);
  };

  /** 金額表記。弥生のエクスポートは3桁区切り（例 "9,120"）。
   *  カンマを含むので csvQuote が引用符で囲む。1000未満はカンマが付かず素の数値になる。 */
  const csvMoney = (n) => csvQuote(Math.round(Number(n) || 0).toLocaleString("en-US"));

  /* 弥生給与CSVの見出し行を Shift-JIS(cp932) のバイト列で持つ。
     参照ファイル docs/yayoikyuyo.csv は弥生がエクスポートした Shift-JIS のため、
     取り込み側も同じ文字コードを前提とする。ブラウザは UTF-8 しかエンコードできず、
     見出しは固定文字列なのでバイト列を直接持たせている。
     データ行は社員コードと数値のみで ASCII なので、そのままバイト化できる。
     見出しを変更するときは Shift-JIS で再エンコードした値に差し替えること。
     元の文字列: 社員コード,２直日数,３直日数,出勤日数,有休日数,実働時間,普通残業時間,休日残業時間,深夜残業時間,日帰り通勤費 */
  const YAYOI_HEADER_SJIS = [
    142,208,136,245,131,82,129,91,131,104,44,130,81,146,188,147,250,144,148,44,
    130,82,146,188,147,250,144,148,44,143,111,139,206,147,250,144,148,44,151,76,
    139,120,147,250,144,148,44,142,192,147,173,142,158,138,212,44,149,129,146,202,
    142,99,139,198,142,158,138,212,44,139,120,147,250,142,99,139,198,142,158,138,
    212,44,144,91,150,233,142,99,139,198,142,158,138,212,44,147,250,139,65,130,232,
    146,202,139,206,148,239,
  ];

  /** 弥生給与向けCSV（Shift-JIS）をダウンロードする。 */
  const downloadYayoiCSV = (filename, dataRows) => {
    const body = dataRows.map((row) => row.join(",")).join("\r\n");
    // データ行は ASCII のみ。非ASCIIが混ざったら Shift-JIS にできないので '?' に落とす。
    const bodyBytes = Array.from(`\r\n${body}\r\n`).map((ch) => {
      const code = ch.charCodeAt(0);
      return code < 128 ? code : 63; // 63 = '?'
    });
    const blob = new Blob([new Uint8Array([...YAYOI_HEADER_SJIS, ...bodyBytes])], {
      type: "text/csv",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const downloadCSV = (filename, csvContent) => {
    const bom = new Uint8Array([0xef, 0xbb, 0xbf]); // UTF-8 BOM
    const blob = new Blob([bom, csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportYayoiCSV = async () => {
    if (selectedEmployeeIds.length === 0) {
      alert("最低1人を選択してください。"); 
      return;
    }

    setLoading(true);

    // 期間の既定値。toISOString() は UTC を返すため、日本時間の 00:00〜09:00 に実行すると
    // 前日の日付になってしまう。ローカル日付で組み立てること。
    // 日付文字列を Date に戻すときも、正午を基準にしてタイムゾーンでの日付ずれを防ぐ。
    const now = new Date();
    const endDate =
      printEndDate || toISODateLocal(now.getFullYear(), now.getMonth(), now.getDate());
    const endD = new Date(`${endDate}T12:00:00`);
    const startDate = printStartDate || toISODateLocal(endD.getFullYear(), endD.getMonth(), 1);

    try {
      // 1. 出力する列は弥生給与のエクスポート形式（docs/yayoikyuyo.csv）に合わせる。
      //    社員コード, ２直日数, ３直日数, 出勤日数, 有休日数, 実働時間,
      //    普通残業時間, 休日残業時間, 深夜残業時間, 日帰り通勤費
      //    見出し行は downloadYayoiCSV が Shift-JIS のバイト列で書き出すので、ここではデータ行のみ作る。
      const csvRows = [];

      // 2. 社員データを取得
      for (const memberId of selectedEmployeeIds) {
        const employeeInfo = employeeRecords.find((e) => e.memberId === memberId);
        if (!employeeInfo) continue;

        try {
          const res = await api.get(`/api/admin/worktime/${memberId}/`, {
            params: { start: startDate, end: endDate },
          });
          const data = res.data || {};
          const summary = data.summary || {};
          const records = data.records || [];

          // 集計値は勤務表の画面と必ず同じ計算にする。
          // 承認済みの残業／遅刻／早退は WorkTimeAdminNote（adminNote）に分単位で入るため、
          // それがある日はそちらを優先する（renderDetailTableRow と同じ優先順位）。
          // ここを r.overtime_hours だけで集計すると、画面に出ている時間が CSV に載らない。
          const sumBy = (fn) => records.reduce((sum, r) => sum + fn(r), 0);

          // 深夜残業（22:00〜翌5:00）＝バックエンドが残業申請の時刻から算出。
          const totalNightOt = sumBy((r) =>
            Number(r.night_overtime_hours ?? r.nightOvertimeHours ?? 0)
          );
          // 総残業（画面と同じ優先順位：管理メモ→残業申請）
          const totalOvertime = sumBy((r) => {
            const mins = r.adminNote?.overtimeMinutes;
            return mins != null ? mins / 60 : Number(r.overtime_hours ?? r.overtimeHours ?? 0);
          });
          // 普通残業時間 ＝ 総残業 − 深夜残業（勤務表の残業列と同じ計算）
          const totalNormalOt = Math.max(0, totalOvertime - totalNightOt);
          // 休日残業時間 ＝ 承認済み休日出勤申請の労働時間（バックエンドが holiday_hours で返す）
          const totalHolidayOt = sumBy((r) =>
            Number(r.holiday_hours ?? r.holiday_overtime_hours ?? 0)
          );

          const totalWorkHours = Number(data.total_hours ?? 0);

          // 弥生給与の列順どおりに出力する（見出しは downloadYayoiCSV 側）
          csvRows.push([
            csvQuote(data.memberId ?? memberId),  // 社員コード
            summary.work2 ?? 0,                   // ２直日数
            summary.work3 ?? 0,                   // ３直日数
            summary.attendance ?? 0,              // 出勤日数
            summary.paid_leave ?? 0,              // 有休日数
            csvNum(totalWorkHours),               // 実働時間
            csvNum(totalNormalOt),                // 普通残業時間（深夜を除く）
            csvNum(totalHolidayOt),               // 休日残業時間
            csvNum(totalNightOt),                 // 深夜残業時間
            csvMoney(summary.transport_round_amount), // 日帰り通勤費
          ]);
        } catch (err) {
          console.error("CSVファイル出力エラー:", memberId, err);
        }
      }

      // 3. 弥生給与向けに Shift-JIS でダウンロードする
      if (csvRows.length === 0) {
        alert("出力できるデータがありませんでした。");
        return;
      }
      downloadYayoiCSV(`yayoi_kintai_${startDate}_to_${endDate}.csv`, csvRows);
    } catch (err) {
      console.error("CSVファイル出力エラー:", err);
      alert("CSV出力中にエラーが発生しました。");
    } finally {
      setLoading(false);
    }
  };

  /** 勤務分析タブのレンダリング */
  const renderMainTabs = () => (
    <div className="ec-tabs no-print">
      <button
        type="button"
        className={`ec-tab${mainTab === "records" ? " ec-tab-active" : ""}`}
        onClick={() => setMainTab("records")}
      >
        日別・部署別
      </button>
      <button
        type="button"
        className={`ec-tab${mainTab === "analytics" ? " ec-tab-active" : ""}`}
        onClick={() => setMainTab("analytics")}
      >
        勤務分析
      </button>
    </div>
  );

  const renderAnalyticsPanel = () => {
    const s = analyticsData?.summary;
    const pct = (v) => (v == null || Number.isNaN(v) ? "—" : `${(v * 100).toFixed(1)}%`);

    return (
      <div>
        <h2 className="mb-3" style={{ fontSize: "1.25rem", fontWeight: 700 }}>
          勤怠率分析
        </h2>
        <p style={{ color: "#64748b", fontSize: "0.9rem", marginBottom: 16 }}>
          シフトに始業・終業が入っている日のみ集計（日別勤怠画面と同じ前提）。色は社員×日の状態です。
        </p>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 12,
            alignItems: "flex-end",
            marginBottom: 20,
          }}
        >
          <label>
            対象月{" "}
            <input
              type="month"
              value={analyticsMonth}
              onChange={(e) => setAnalyticsMonth(e.target.value)}
              style={{ marginLeft: 6, padding: 4 }}
            />
          </label>
          <label>
            部署{" "}
            <select
              value={analyticsDept}
              onChange={(e) => setAnalyticsDept(e.target.value)}
              style={{ marginLeft: 6, padding: 4, minWidth: 200 }}
            >
              <option value="__ALL__">全社</option>
              {analyticsDepartmentOptions.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={fetchAttendanceAnalytics}
            disabled={analyticsLoading}
          >
            {analyticsLoading ? "更新中…" : "再取得"}
          </button>
        </div>

        {analyticsError && (
          <div className="alert alert-danger" role="alert">
            {analyticsError}
          </div>
        )}

        {analyticsData && s && (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
                gap: 12,
                marginBottom: 24,
              }}
            >
              <div style={{ background: "#f8fafc", borderRadius: 8, padding: 12, border: "1px solid #e2e8f0" }}>
                <div style={{ fontSize: "0.75rem", color: "#64748b" }}>出退勤率（両方打刻）</div>
                <div style={{ fontSize: "1.35rem", fontWeight: 700 }}>{pct(s.attendanceRate)}</div>
              </div>
              <div style={{ background: "#f8fafc", borderRadius: 8, padding: 12, border: "1px solid #e2e8f0" }}>
                <div style={{ fontSize: "0.75rem", color: "#64748b" }}>遅刻率（予定日比）</div>
                <div style={{ fontSize: "1.35rem", fontWeight: 700 }}>{pct(s.lateRate)}</div>
              </div>
              <div style={{ background: "#f8fafc", borderRadius: 8, padding: 12, border: "1px solid #e2e8f0" }}>
                <div style={{ fontSize: "0.75rem", color: "#64748b" }}>欠打刻・未完了率</div>
                <div style={{ fontSize: "1.35rem", fontWeight: 700 }}>{pct(s.absenceOrIncompleteRate)}</div>
              </div>
              {/*}
              <div style={{ background: "#f8fafc", borderRadius: 8, padding: 12, border: "1px solid #e2e8f0" }}>
                <div style={{ fontSize: "0.75rem", color: "#64748b" }}>予定あった人日</div>
                <div style={{ fontSize: "1.35rem", fontWeight: 700 }}>{s.scheduledPersonDays ?? 0}</div>
              </div>
              */}
            </div>

            <h3 style={{ fontSize: "1rem", marginBottom: 8 }}>日別サマリ</h3>
            <div style={{ overflowX: "auto", marginBottom: 24 }}>
              <table className="table table-sm table-bordered" style={{ fontSize: "0.85rem", whiteSpace: "nowrap" }}>
                <thead>
                  <tr>
                    <th>日付</th>
                    <th>予定</th>
                    <th>正常</th>
                    <th>遅刻</th>
                    <th>出勤なし</th>
                    <th>退勤未完</th>
                  </tr>
                </thead>
                <tbody>
                  {(analyticsData.byDay || []).map((row) => (
                    <tr key={row.date}>
                      <td>{row.date}</td>
                      <td>{row.scheduled}</td>
                      <td>{row.ok}</td>
                      <td>{row.late}</td>
                      <td>{row.absent}</td>
                      <td>{row.incomplete}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <div style={{ marginBottom: 8, fontWeight: 600 }}>凡例（各セル 左＝出勤 / 右＝退勤）</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 16, fontSize: "0.85rem" }}>
              {HEATMAP_LEGEND.map((it) => (
                <span key={it.label} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <span
                    style={{
                      width: 14,
                      height: 14,
                      borderRadius: 2,
                      background: it.color,
                      border: "1px solid #cbd5e1",
                    }}
                  />
                  {it.label}
                </span>
              ))}
            </div>
            
            <h3 style={{ fontSize: "1rem", marginBottom: 8 }}>ヒートマップ（社員×日）</h3>
            <div style={{ overflowX: "auto", maxHeight: "70vh", border: "1px solid #e5e7eb", borderRadius: 8 }}>
              <table style={{ borderCollapse: "collapse", fontSize: "11px" }}>
                <thead>
                  <tr>
                    <th
                      style={{
                        position: "sticky",
                        left: 0,
                        background: "#1e3a5f",
                        color: "#fff",
                        zIndex: 2,
                        padding: "6px 8px",
                        minWidth: 120,
                      }}
                    >
                      氏名 / コード
                    </th>
                    {(analyticsData.monthDates || []).map((ds) => (
                      <th
                        key={ds}
                        style={{
                          padding: "4px 2px",
                          minWidth: 22,
                          background: "#334155",
                          color: "#fff",
                          fontWeight: 600,
                        }}
                        title={ds}
                      >
                        {ds.slice(8, 10)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(analyticsData.heatmap || []).map((row) => (
                    <tr key={row.memberId}>
                      <td
                        style={{
                          position: "sticky",
                          left: 0,
                          background: "#fff",
                          zIndex: 1,
                          border: "1px solid #e5e7eb",
                          padding: "4px 8px",
                          minWidth: 120,
                        }}
                      >
                        <div style={{ fontWeight: 600 }}>{row.name || "—"}</div>
                        <div style={{ color: "#64748b", fontSize: "10px" }}>{row.memberId}</div>
                      </td>
                      {(row.cells || []).map((cell, idx) => {
                        const inK = heatmapInState(cell);
                        const outK = heatmapOutState(cell);
                        return (
                          <td
                            key={idx}
                            title={`${analyticsData.monthDates[idx] || ""} 出勤:${HEATMAP_IN_LABELS[inK]} / 退勤:${HEATMAP_OUT_LABELS[outK]}`}
                            style={{
                              border: "1px solid #e5e7eb",
                              width: 22,
                              height: 22,
                              padding: 0,
                            }}
                          >
                            <div style={{ display: "flex", width: "100%", height: "100%" }}>
                              <div style={{ flex: 1, background: HEATMAP_IN_COLORS[inK] }} />
                              <div style={{ flex: 1, background: HEATMAP_OUT_COLORS[outK] }} />
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {!analyticsLoading && analyticsData && (analyticsData.heatmap || []).length === 0 && (
          <p className="text-muted">対象データがありません（シフト未登録の日のみの場合など）。</p>
        )}
      </div>
    );
  };

  // ===== 部署選択画面 =====
  const renderDepartmentSelection = () => (
    <div className="worktimes-container selection-screen ec-page">
      <div className="ec-header">
        <h2 className="ec-title">出退勤管理</h2>
        <div className="ec-header-actions">
          <button type="button" className="ec-btn-back" onClick={handleBack}>
            メニュー画面に戻る
          </button>
        </div>
      </div>
      {renderMainTabs()}
        <div className="selection-controls">
            {/* 部署選択: xx00のみ、00xxは表示しない */}
            <div className="department-select-group">
               <label htmlFor="dept-filter">部署:</label>
               <select
                    id="dept-filter"
                    value={selectedDeptId}
                    onChange={handleDepartmentChange}
                    className="select-department"
                >
                    <option value="">全ての部署</option>
                    {(departments || [])
                        .filter((d) => d.departmentId && d.departmentId.endsWith("00") && !d.departmentId.startsWith("00"))
                        .sort((a, b) => (a.departmentName || "").localeCompare(b.departmentName || ""))
                        .map((d) => (
                          <option key={d.departmentId} value={d.departmentId}>{d.departmentName}</option>
                        ))}
                </select>
            </div>
            {selectedDeptId && (
              <div className="department-select-group">
                <label htmlFor="ka-filter">課:</label>
                <select id="ka-filter" value={selectedKaName} onChange={handleKaChange} className="select-department">
                  <option value="">課を選択</option>
                  {((selectedDept && selectedDept.children) || [])
                    .filter((c) => c.departmentName)
                    .sort((a, b) => (a.departmentName || "").localeCompare(b.departmentName || ""))
                    .map((c) => (
                      <option key={c.departmentId || c.departmentName} value={c.departmentName}>{c.departmentName}</option>
                    ))}
                </select>
              </div>
            )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: 6, flexWrap: 'wrap' }}>
          <Link
            to="/admin/worktime-daily"
            state={{ date: displayDate, departmentName: effectiveDepartmentName || undefined }}
            className="forgot-punch-btn"
            style={{
              display: 'inline-block',
              background: '#c62828',
              color: '#fff',
              padding: '10px 28px',
              borderRadius: 8,
              fontWeight: 'bold',
              fontSize: '1rem',
              textDecoration: 'none',
            }}
          >
            打刻忘れ管理（{forgotPunchRows.length}件）
          </Link>
          <Link
            to={scheduleUnregisteredHref}
            style={{
              display: "inline-block",
              background: "#1565c0",
              color: "#fff",
              padding: "10px 28px",
              borderRadius: 8,
              fontWeight: "bold",
              fontSize: "1rem",
              textDecoration: "none",
            }}
          >
            スケジュール管理{unregisteredScheduleRows.length > 0 ? ` (${unregisteredScheduleRows.length}件)` : ""}
          </Link>
        </div>

        {employeeRecords.length > 0 && (
            <div className="employee-list-section">
                {renderEmployeeList()} 
            </div>
        )}
    </div>
  );

  // ===== 部署選択した社員情報を表示  =====
  const renderEmployeeList = () => (
    <>
      <h2 className="worktimes-title worktimes-title-sub">
        {selectedDepartmentName
          ? `${selectedDepartmentName}（${formatJapaneseDate(displayDate)}）`
          : formatJapaneseDate(displayDate)}
      </h2>

      <div className="detail-footer-buttons detail-footer-buttons-above-table">
          <div className="worktimes-date-range" style={{ display: "inline-flex", alignItems: "center", flexWrap: "nowrap", gap: 12 }}>
            <label style={{ display: "inline-flex", alignItems: "center", gap: 8, whiteSpace: "nowrap" }}>
              開始日:
              <input
                type="date"
                value={printStartDate}
                onChange={(e) => setPrintStartDate(e.target.value)}
                style={{ padding: 4 }}
                placeholder="省略時→月初"
              />
            </label>
            <label style={{ display: "inline-flex", alignItems: "center", gap: 8, whiteSpace: "nowrap" }}>
              終了日:
              <input
                type="date"
                value={printEndDate}
                onChange={(e) => setPrintEndDate(e.target.value)}
                style={{ padding: 4 }}
              />
            </label>
          </div>

          {/* ──── CSV ──── */}
          <button
            type="button"
            className="btn-print"
            style={{ backgroundColor: "#107c41", color: "#fff", borderColor: "#107c41" }}
            onClick={handleExportYayoiCSV}
            disabled={selectedEmployeeIds.length === 0}
          >
            {`弥生CSV出力: ${selectedEmployeeIds.length}`}
          </button>

          <button
            className="btn-print green-print-btn"
            onClick={handlePrintSelected}
            disabled={selectedEmployeeIds.length === 0}
          >
            {`印刷: ${selectedEmployeeIds.length}`}
          </button>
          <button
            type="button"
            className="back-btn blue-back-btn"
            onClick={() =>
              navigate("/admin/labor-hours-list", {
                state: {
                  departmentName: (effectiveDepartmentName || selectedDepartmentName) || undefined,
                  printEndDate,
                },
              })
            }
          >
            労働時間一覧
          </button>
      </div>

      <table className="employee-table">
        <thead>
          <tr>
            <th>
              <input
                type="checkbox"
                checked={selectAll}
                onChange={handleSelectAll}
              />
            </th>
            <th>コード </th>
            <th>氏名 </th>
            <th>打刻時刻 </th>
            <th>当月合計労働時間 </th>
          </tr>
        </thead>
        <tbody>
          {employeeRecords.map((r) => (
            <tr key={r.memberId}>
              <td>
                <input
                  type="checkbox"
                  checked={selectedEmployeeIds.includes(r.memberId)}
                  onChange={() => handleSelectEmployee(r.memberId)}
                />
              </td>
              {/*}
              <td>
                <button
                  className="link-button" 
                  onClick={() => fetchEmployeeDetail(r.memberId)}
                >
                  {r.memberId}
                </button>
              </td>
              */}
              <td onClick={() => fetchEmployeeDetail(r.memberId)}>{r.memberId}</td>
              <td>{r.name}</td>
              <td
                style={
                  r.admin_punch_modified
                    ? { color: "#c62828", fontWeight: "bold" }
                    : undefined
                }
              >
                {(() => {
                  const rest = scheduleRestDisplay(r);
                  if (rest) return rest.text;
                  return r.punchTimeDisplay != null && r.punchTimeDisplay !== ""
                    ? r.punchTimeDisplay
                    : r.start_time
                      ? formatTimeHhMm(r.start_time)
                      : "未登録";
                })()}
                {r.admin_punch_modified ? "※" : ""}
              </td>
              <td>{r.monthly_total_to_yesterday != null ? `${Number(r.monthly_total_to_yesterday).toFixed(2)}h` : "-"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );

  const renderPrintView = () => {
    const todayStr = new Date().toISOString().split("T")[0];
    const displayDateLabel = formatJapaneseDate(todayStr);
    const pr0 = printData[0]?.printRange;
    const printMonthTitle =
      pr0?.end
        ? `${new Date(`${pr0.end.slice(0, 10)}T12:00:00`).getMonth() + 1}月`
        : `${new Date().getMonth() + 1}月`;
    return (
      <div className="worktimes-container print-view">
        <div className="detail-footer-buttons no-print">
          <button className="back-btn blue-back-btn" onClick={() => setIsPrinting(false)}>
            戻る
          </button>
          <button className="btn-print green-print-btn" onClick={() => window.print()}>
            印刷
          </button>
        </div>

        {/* 画面表示用: ヘッダは1つだけ（個別労働時間） */}
        <div className="print-view-single-header no-print-header">
          <h2 style={{ textAlign: 'center', color: '#0d47a1' }}>{printMonthTitle}勤務表</h2>
          <p style={{ textAlign: "center" }}>
            {selectedDepartmentName ? `部署名: ${selectedDepartmentName} | ` : ""}
            期間: {formatPrintPeriod(printData[0]?.printRange)} / 印刷準備日: {displayDateLabel}
          </p>
        </div>

        {printData.map((emp) => {
          const records = emp.records || [];
          const byMonth = {};
          records.forEach((r) => {
            const monthKey = (r.date || "").slice(0, 7);
            if (!byMonth[monthKey]) byMonth[monthKey] = [];
            byMonth[monthKey].push(r);
          });
          const months = Object.keys(byMonth).sort();
          if (months.length === 0) {
            const endYm = (emp.printRange?.end || "").slice(0, 7);
            months.push(endYm || new Date().toISOString().slice(0, 7));
          }
          return (
            <div key={emp.memberId} className="employee-print-section" style={{ borderTop: '3px solid #0d47a1', paddingTop: 8, marginTop: 24, maxWidth: 1200, margin: '24px auto 0' }}>
              {months.map((monthKey, mi) => {
                const pageRecords = (byMonth[monthKey] || []).slice().sort((a, b) =>
                  (a.date || "").localeCompare(b.date || "")
                );
                const [, mPart] = monthKey.split("-");
                const monthLabel = `${parseInt(mPart, 10)}月`;
                const monthTotal = pageRecords.reduce(
                  (s, r) => s + calcRowTotalHours(r),
                  0
                );
                const firstD = pageRecords[0]?.date;
                const lastD = pageRecords[pageRecords.length - 1]?.date;
                const monthPeriodShort =
                  firstD && lastD
                    ? `${formatMonthDayShort(firstD)}～${formatMonthDayShort(lastD)}`
                    : "";
                return (
                  <div
                    key={monthKey}
                    className="print-month-one-page"
                    style={{ marginTop: mi === 0 ? 0 : 16 }}
                  >
                    <div className="print-only-per-person-header">
                      <h2 className="print-month-title print-only">{monthLabel}勤務表</h2>
                      <div className="detail-header-row print-month-subhead">
                        <div className="employee-detail-name print-month-meta">
                          <span>
                            {emp.departmentName ? `${emp.departmentName}　` : ""}
                            {emp.memberId}　{emp.name} ／ {monthPeriodShort}（{pageRecords.length} 日）／ 労働時間計{" "}
                            <strong>{monthTotal.toFixed(2)}</strong> 時間（土日・休み・未打刻日を含む）※は管理者修正
                          </span>
                          <span>{displayDateLabel}</span>
                        </div>
                      </div>
                    </div>
                    <table className="employee-detail-table employee-detail-table-print-calendar">
                      {renderDetailTableHeader()}
                      <tbody>
                        {pageRecords.map((r, index) => renderDetailTableRow(r, index))}
                      </tbody>
                    </table>
                    {renderWorkSummaryTable(emp.summary)}
                    {/*}
                    <div className="total-hours print-month-footer">
                      {monthLabel} 行合計 {monthTotal.toFixed(2)} 時間（土日・休み・未打刻日を含む）
                      <span style={{ fontSize: "0.85em", color: "#666", marginLeft: 8 }}>
                        ※は管理者修正
                      </span>
                    </div>
                    */}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    );
  };

  // ===== 社員詳細  =====
  // 勤務管理（月次集計）テーブル。バックエンドの summary をそのまま表示する。
  const renderWorkSummaryTable = (summary) => {
    if (!summary) return null;
    const s = summary;
    const cell = { border: "1px solid #333", padding: "3px 10px", textAlign: "center", whiteSpace: "nowrap" };
    const head = { ...cell, background: "#f1f5f9", fontWeight: "bold" };
    const leaveRows = [
      ["指定休", s.designated_off],
      ["有休", s.paid_leave],
      ["特休", s.special_leave],
      ["半休", s.half_day],
      ["振替休", s.substitute_off],
      ["欠勤", s.absence],
      ["休日出勤", s.holiday_work],
      ["その他", s.other],
      ["出勤", s.attendance],
    ];
    return (
      <div className="work-summary-wrap" style={{ marginTop: 16, display: "flex", gap: 24, flexWrap: "wrap", justifyContent: "flex-end", alignItems: "flex-start" }}>
        {/* 勤務日数 */}
        <table style={{ borderCollapse: "collapse", fontSize: "0.85rem" }}>
          <tbody>
            <tr><th colSpan={2} style={head}>勤務管理 — 勤務日数</th></tr>
            <tr><th style={head}>勤務</th><th style={head}>日数</th></tr>
            {leaveRows.map(([label, val]) => (
              <tr key={label}>
                <td style={head}>{label}</td>
                <td style={cell}>{val ?? 0}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* 勤務手当て + 特休内訳 */}
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <table style={{ borderCollapse: "collapse", fontSize: "0.85rem" }}>
            <tbody>
              <tr><th colSpan={5} style={head}>勤務手当て</th></tr>
              <tr>
                <th style={head}>1勤務</th>
                <th style={head}>2勤務</th>
                <th style={head}>3勤務</th>
                <th style={head}>その他</th>
                <th style={head}>休日出勤</th>
              </tr>
              <tr>
                <td style={cell}>{s.work1}</td>
                <td style={cell}>{s.work2}</td>
                <td style={cell}>{s.work3}</td>
                <td style={cell}>{s.work_other}</td>
                <td style={cell}>{s.holiday_work_allowance}</td>
              </tr>
            </tbody>
          </table>
          <table style={{ borderCollapse: "collapse", fontSize: "0.85rem" }}>
            <tbody>
              <tr><th colSpan={6} style={head}>特休 内訳</th></tr>
              <tr>
                <th style={head}>結婚</th>
                <td style={cell}>{s.special_marriage}</td>
                <th style={head}>忌服</th>
                <td style={cell}>{s.special_bereavement}</td>
                <th style={head}>出産</th>
                <td style={cell}>{s.special_childbirth}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderEmployeeDetail = () => {
    if (!employeeDetail) return null;

    const displayDetailDate = detailDateRange.end
      ? formatJapaneseDate(detailDateRange.end)
      : formatJapaneseDate(new Date().toISOString());

    const ds = detailDateRange.start?.slice(0, 10) || "";
    const de = detailDateRange.end?.slice(0, 10) || "";
    const recordsInRange = (employeeDetail.records || []).filter((r) => {
      const d = (r.date || "").slice(0, 10);
      return d && d >= ds && d <= de;
    });
    const detailExpanded = expandRecordsToCalendarDays(
      recordsInRange,
      detailDateRange.start,
      detailDateRange.end
    ).sort((a, b) => (a.date || "").localeCompare(b.date || ""));

    const detailMonthTotal = detailExpanded.reduce(
      (s, r) => s + calcRowTotalHours(r),
      0
    );
    const d0 = detailExpanded[0]?.date;
    const d1 = detailExpanded[detailExpanded.length - 1]?.date;
    const detailPeriodShort =
      d0 && d1 ? `${formatMonthDayShort(d0)}～${formatMonthDayShort(d1)}` : "";

    return (
      <div className="worktimes-container employee-detail-root ec-page">
        {/*}
        <div className="ec-header no-print">
          <h2 className="ec-title">出退勤管理</h2>
          <div className="ec-header-actions">
            <button type="button" className="ec-btn-back" onClick={handleBack}>
              メニュー画面に戻る
            </button>
          </div>
        </div>
        */}
        {renderMainTabs()}
        <div className="detail-header-row no-print employee-detail-screen-subhead">
          <h2 className="employee-detail-name">
            {displayDetailDate}:
            {selectedDepartmentName ? `${selectedDepartmentName} ` : ""}（{employeeDetail.name}）- 勤務表：{detailMonthTotal.toFixed(2)}{" "}
            時間
          </h2>
        </div>

        {/* 表示期間の指定（未指定なら当月1日〜当日。APIは任意期間に対応済み） */}
        <div className="no-print" style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12, margin: "0 0 12px" }}>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
            開始日:
            <input type="date" value={printStartDate} onChange={(e) => setPrintStartDate(e.target.value)} style={{ padding: 4 }} />
          </label>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
            終了日:
            <input type="date" value={printEndDate} onChange={(e) => setPrintEndDate(e.target.value)} style={{ padding: 4 }} />
          </label>
          <button
            type="button"
            onClick={() => {
              const endD = printEndDate || displayDate;
              const startD =
                printStartDate ||
                (() => {
                  const d = new Date(`${endD}T12:00:00`);
                  return toISODateLocal(d.getFullYear(), d.getMonth(), 1);
                })();
              fetchEmployeeDetail(employeeDetail.memberId, startD, endD);
            }}
            style={{ padding: "6px 14px", background: "#2563eb", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600 }}
          >
            この期間で表示
          </button>
          <span style={{ color: "#6b7280", fontSize: "0.8rem" }}>
            （未指定なら当月1日〜{formatJapaneseDate(displayDate)}）
          </span>
        </div>

        <div className="print-month-one-page employee-detail-print-wrap employee-detail-print-bundle">
          {/* 印刷時のみ表示するヘッダー（社員コード・氏名・時点・合計時間） */}
          <div className="employee-detail-print-info-header">
            <h2 className="employee-detail-print-info-title">勤務表</h2>
            <div className="employee-detail-print-info-row">
              <span>社員コード：{employeeDetail.memberId}</span>
              <span>氏名：{employeeDetail.name}</span>
              <span>期間：{detailPeriodShort}（{detailExpanded.length}日）</span>
              <span>合計：<strong>{detailMonthTotal.toFixed(2)}</strong> 時間</span>
            </div>
          </div>

          <div className="employee-detail-table-scroll-wrap">
          <table className="employee-detail-table employee-detail-table-print-calendar">
            {renderDetailTableHeader()}
            <tbody>
              {detailExpanded.map((r, index) => renderDetailTableRow(r, index))}
            </tbody>
          </table>
          </div>

          {/* 勤務管理（勤務日数・勤務手当て・特休内訳の月次集計） */}
          {renderWorkSummaryTable(employeeDetail.summary)}
          {/*}
          <div className="total-hours print-month-footer employee-detail-print-footer">
            合計 {detailMonthTotal.toFixed(2)} 時間（土日・休み・未打刻日を含む）※は管理者修正
          </div>
          */}
        </div>

        <div className="detail-footer-buttons no-print">
          <button
            className="back-btn blue-back-btn" 
            onClick={() => {
              setEmployeeDetail(null);
              setViewMode("employee_list");
            }}
          >
            戻る
          </button>
          <button className="btn-print green-print-btn" onClick={() => window.print()}> 
            印刷
          </button>
        </div>
      </div>
    );
  };
  
  

  // ===== 表示 =====
  if (mainTab === "analytics") {
    return (
      <div className="worktimes-container ec-page">
        <div className="ec-header">
          <h2 className="ec-title">出退勤管理</h2>
          <div className="ec-header-actions">
            <button type="button" className="ec-btn-back" onClick={handleBack}>
              戻る
            </button>
          </div>
        </div>
        {renderMainTabs()}
        {renderAnalyticsPanel()}
        <div className="detail-footer-buttons" style={{ marginTop: 24, justifyContent: "center" }}>
          <Link to="/dashboard" className="back-btn blue-back-btn">
            メニュー画面に戻る
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="worktimes-container ec-page">
        <div className="ec-header">
          <h2 className="ec-title">出退勤管理</h2>
          <div className="ec-header-actions">
            <button type="button" className="ec-btn-back" onClick={handleBack}>
              メニュー画面に戻る
            </button>
          </div>
        </div>
        {renderMainTabs()}
        <div style={{ padding: "24px 16px" }}>ロード中...</div>
      </div>
    );
  }

  if (isPrinting) return renderPrintView();
  if (viewMode === "employee_detail") return renderEmployeeDetail();
  return renderDepartmentSelection();
};

export default WorkTimes;